import {DndContext, DragOverlay} from '@dnd-kit/core';
import {SortableContext, SortingStrategy} from '@dnd-kit/sortable';
import {Grid, GridItem, Integer, Show, ValueByDevice} from 'platform/foundation';
import styled from 'styled-components';

import {isNotNil} from 'ramda';

import {suffixTestId, TestIdProps} from 'shared/web';

import {DASHBOARD_ROW_UNIT} from '../../consts/dashboardRowUnit';
import {useDashboardAdaptiveDensityBoard} from '../../hooks/useDashboardAdaptiveDensityBoard';
import {useDashboardDragAndDrop} from '../../hooks/useDashboardDragAndDrop';
import {DashboardTile} from '../../types/DashboardTile';
import {DashboardTileId} from '../../types/DashboardTileId';
import {DashboardTileSize} from '../../types/DashboardTileSize';
import {DASHBOARD_GRID_COLUMNS} from '../../utils/allocateDashboardGrid';
import {
  DashboardDropTransaction,
  getDashboardLayoutSignature,
} from '../../utils/projectDashboardDrop';
import {toDashboardGridPlacement} from '../../utils/toDashboardGridPlacement';
import {DashboardAdaptiveDensityContext} from '../DashboardAdaptiveDensityContext';
import {DashboardDropGhost} from './DashboardDropGhost';
import {DashboardDropIndicator} from './DashboardDropIndicator';
import {DashboardGapSlot} from './DashboardGapSlot';
import {DashboardTileContent} from './DashboardTileContent';
import {SortableDashboardTile} from './SortableDashboardTile';

interface DashboardGridProps extends TestIdProps {
  tiles: DashboardTile[];
  isCustomising: boolean;
  onDrop: (transaction: DashboardDropTransaction) => void;
  onResize: (id: DashboardTileId, size: DashboardTileSize) => void;
  onHide: (id: DashboardTileId) => void;
}

const DRAG_PREVIEW_SCALE = 1.02;
const GRID_COLUMNS: ValueByDevice<number> = [
  1,
  DASHBOARD_GRID_COLUMNS,
  DASHBOARD_GRID_COLUMNS,
  DASHBOARD_GRID_COLUMNS,
];
const GRID_SPACING: ValueByDevice<Integer> = [3, 4, 4, 4];

const ROW_TRACK = `minmax(${DASHBOARD_ROW_UNIT}, auto)` as const;

const GRID_AUTO_ROWS: ValueByDevice<typeof ROW_TRACK | 'auto'> = [
  'auto',
  ROW_TRACK,
  ROW_TRACK,
  ROW_TRACK,
];

// Deliberately no-op: the board stays still during a drag, so neighbours must get no transforms.
const FIXED_LAYOUT_STRATEGY: SortingStrategy = () => null;

const StyledMeasurementRoot = styled.div`
  min-width: 0;
`;

const StyledDragPreview = styled.div`
  height: 100%;
  transform: scale(${DRAG_PREVIEW_SCALE});
  box-shadow: ${({theme}) => theme.shadows.elevation_3};
  border-radius: ${({theme}) => theme.radii.medium};
  cursor: grabbing;
  pointer-events: none;
`;

export function DashboardGrid(props: DashboardGridProps) {
  const layoutSignature = getDashboardLayoutSignature(props.tiles);
  const {
    contextValue: adaptiveDensityContextValue,
    setRoot: setAdaptiveDensityRoot,
    setDragging: setAdaptiveDensityDragging,
  } = useDashboardAdaptiveDensityBoard(layoutSignature);
  const dragAndDrop = useDashboardDragAndDrop({
    tiles: props.tiles,
    layoutSignature,
    onDrop: props.onDrop,
    onDraggingChange: setAdaptiveDensityDragging,
  });
  const preview = dragAndDrop.preview;

  return (
    <DndContext {...dragAndDrop.contextProps}>
      <SortableContext
        items={props.tiles.map((tile) => tile.id)}
        strategy={FIXED_LAYOUT_STRATEGY}
        disabled={!props.isCustomising}
      >
        <DashboardAdaptiveDensityContext.Provider value={adaptiveDensityContextValue}>
          <StyledMeasurementRoot ref={setAdaptiveDensityRoot}>
            <Grid
              columns={GRID_COLUMNS}
              spacing={GRID_SPACING}
              autoRows={GRID_AUTO_ROWS}
              autoFlow="row"
              data-testid={suffixTestId('dashboardGrid', props)}
            >
              {props.tiles.map((tile) => (
                <GridItem key={tile.id} {...toDashboardGridPlacement(tile)}>
                  <SortableDashboardTile
                    tile={tile}
                    isCustomising={props.isCustomising}
                    isVacating={preview.exchangeCounterparts.some(
                      (counterpart) => counterpart.id === tile.id
                    )}
                    onResize={props.onResize}
                    onHide={props.onHide}
                    data-testid={suffixTestId(`tile-${tile.id}`, props)}
                  />
                </GridItem>
              ))}
              <Show when={dragAndDrop.activeTile}>
                {(activeTile) =>
                  dragAndDrop.gapSlots.map((slot) => (
                    <DashboardGapSlot
                      key={`${slot.column}-${slot.row}`}
                      slot={slot}
                      size={activeTile.size}
                      data-testid={suffixTestId(`gapSlot-${slot.column}-${slot.row}`, props)}
                    />
                  ))
                }
              </Show>
              <DashboardDropGhost
                cell={preview.exchangeLanding}
                placement={preview.placement}
                data-testid={suffixTestId('dropGhost', props)}
              />
              {preview.exchangeCounterparts.map((counterpart) => (
                <DashboardDropGhost
                  key={counterpart.id}
                  cell={counterpart.cell}
                  label={counterpart.label}
                  data-testid={suffixTestId(`exchangeGhost-${counterpart.id}`, props)}
                />
              ))}
              <DashboardDropGhost
                cell={dragAndDrop.invalidTarget}
                isInvalid
                data-testid={suffixTestId('invalidDropGhost', props)}
              />
              <DashboardDropIndicator
                row={preview.insertionRow}
                isTrailing={preview.isTrailingInsertion}
                data-testid={suffixTestId('dropIndicator', props)}
              />
            </Grid>
          </StyledMeasurementRoot>
        </DashboardAdaptiveDensityContext.Provider>
      </SortableContext>
      <DragOverlay dropAnimation={null}>
        <Show when={isNotNil(dragAndDrop.activeTile)}>
          <StyledDragPreview data-testid={suffixTestId('dragPreview', props)}>
            <DashboardTileContent tile={dragAndDrop.activeTile} />
          </StyledDragPreview>
        </Show>
      </DragOverlay>
    </DndContext>
  );
}
